import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { PageHeader } from '../components/PageHeader';
import { AlertCircleIcon, CalendarIcon, ShieldCheckIcon, ClockIcon } from 'lucide-react';
interface CancellationPolicyPageProps {
  onNavigate: (page: string) => void;
}
export function CancellationPolicyPage({
  onNavigate
}: CancellationPolicyPageProps) {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  return <main className="flex-grow w-full bg-brand-gray" data-id="element-647">
      <PageHeader title="Cancellation Policy" breadcrumb="Cancellation Policy" onNavigateHome={() => onNavigate('home')} data-id="element-648" />

      <section className="py-16 md:py-24" data-id="element-649">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-650">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden" data-id="element-651">
            {/* Key Highlight */}
            <div className="bg-brand-navy p-6 md:p-8" data-id="element-652">
              <div className="flex items-start gap-4" data-id="element-653">
                <div className="w-12 h-12 rounded-xl bg-brand-red/20 flex items-center justify-center shrink-0" data-id="element-654">
                  <AlertCircleIcon className="w-6 h-6 text-brand-red" data-id="element-655" />
                </div>
                <div data-id="element-656">
                  <h2 className="text-xl font-bold text-white mb-2" data-id="element-657">
                    48-Hour Cancellation Window
                  </h2>
                  <p className="text-white/70 text-sm leading-relaxed" data-id="element-658">
                    All cancellations must be made at least 48 hours before your
                    rental start date to receive a full refund.
                  </p>
                </div>
              </div>
            </div>

            {/* Policy Content */}
            <div className="p-6 md:p-8 space-y-8" data-id="element-659">
              <div data-id="element-660">
                <p className="text-brand-charcoal/80 leading-relaxed" data-id="element-661">
                  If for any reason you need to cancel your reservation with us,
                  we simply require that you cancel your reservation{' '}
                  <strong className="text-brand-navy" data-id="element-662">48 hours prior</strong> to
                  your rental date. If you fail to cancel your reservation 48
                  hours prior to your rental date, Scooterplus will retain the
                  rental amount.
                </p>
              </div>

              {/* Example Box */}
              <div className="bg-brand-gray rounded-xl p-5 border border-gray-100" data-id="element-663">
                <div className="flex items-center gap-2 mb-3" data-id="element-664">
                  <CalendarIcon className="w-5 h-5 text-brand-red" data-id="element-665" />
                  <h3 className="font-bold text-brand-navy" data-id="element-666">Example</h3>
                </div>
                <p className="text-brand-charcoal/70 text-sm leading-relaxed" data-id="element-667">
                  If your rental date is{' '}
                  <strong className="text-brand-navy" data-id="element-668">08/11/2010</strong>, your
                  reservation must be canceled by{' '}
                  <strong className="text-brand-navy" data-id="element-669">08/09/2010</strong> to
                  receive a full refund.
                </p>
              </div>

              {/* Late Cancellation */}
              <div className="bg-amber-50 rounded-xl p-5 border border-amber-100" data-id="element-670">
                <div className="flex items-center gap-2 mb-3" data-id="element-671">
                  <ClockIcon className="w-5 h-5 text-amber-600" data-id="element-672" />
                  <h3 className="font-bold text-brand-navy" data-id="element-673">
                    Late Cancellation (Within 48 Hours)
                  </h3>
                </div>
                <p className="text-brand-charcoal/70 text-sm leading-relaxed" data-id="element-674">
                  As a courtesy to our customers, if for any reason you request
                  cancellation within 48 hours of your rental start date and you
                  did not purchase our cancellation protection plan, you will
                  receive a{' '}
                  <strong className="text-brand-navy" data-id="element-675">
                    credit to be used towards a future rental
                  </strong>
                  . The credit must be used within{' '}
                  <strong className="text-brand-navy" data-id="element-676">180 days</strong>.
                </p>
              </div>

              {/* Protection Plan */}
              <div className="bg-emerald-50 rounded-xl p-5 border border-emerald-100" data-id="element-677">
                <div className="flex items-center gap-2 mb-3" data-id="element-678">
                  <ShieldCheckIcon className="w-5 h-5 text-emerald-600" data-id="element-679" />
                  <h3 className="font-bold text-brand-navy" data-id="element-680">
                    Cancellation Protection Plan
                  </h3>
                </div>
                <p className="text-brand-charcoal/70 text-sm leading-relaxed" data-id="element-681">
                  Consider purchasing our cancellation protection plan at
                  checkout for added flexibility. With this plan, you can cancel
                  at any time and receive a full refund regardless of the
                  cancellation window.
                </p>
              </div>

              {/* Quick Summary */}
              <div className="border-t border-gray-100 pt-8" data-id="element-682">
                <h3 className="font-bold text-brand-navy mb-4" data-id="element-683">
                  Quick Summary
                </h3>
                <div className="space-y-3" data-id="element-684">
                  <div className="flex items-start gap-3" data-id="element-685">
                    <div className="w-6 h-6 rounded-full bg-green-100 flex items-center justify-center shrink-0 mt-0.5" data-id="element-686">
                      <svg className="w-4 h-4 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-687">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" data-id="element-688" />
                      </svg>
                    </div>
                    <p className="text-sm text-brand-charcoal/80" data-id="element-689">
                      <strong className="text-brand-navy" data-id="element-690">
                        Cancel 48+ hours before
                      </strong>{' '}
                      → Full refund
                    </p>
                  </div>
                  <div className="flex items-start gap-3" data-id="element-691">
                    <div className="w-6 h-6 rounded-full bg-amber-100 flex items-center justify-center shrink-0 mt-0.5" data-id="element-692">
                      <ClockIcon className="w-3.5 h-3.5 text-amber-600" data-id="element-693" />
                    </div>
                    <p className="text-sm text-brand-charcoal/80" data-id="element-694">
                      <strong className="text-brand-navy" data-id="element-695">
                        Cancel within 48 hours (no protection plan)
                      </strong>{' '}
                      → Rental credit valid for 180 days
                    </p>
                  </div>
                  <div className="flex items-start gap-3" data-id="element-696">
                    <div className="w-6 h-6 rounded-full bg-red-100 flex items-center justify-center shrink-0 mt-0.5" data-id="element-697">
                      <svg className="w-4 h-4 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-698">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" data-id="element-699" />
                      </svg>
                    </div>
                    <p className="text-sm text-brand-charcoal/80" data-id="element-700">
                      <strong className="text-brand-navy" data-id="element-701">
                        No cancellation / no-show
                      </strong>{' '}
                      → Rental amount retained
                    </p>
                  </div>
                </div>
              </div>

              {/* Contact CTA */}
              <div className="border-t border-gray-100 pt-8 text-center" data-id="element-702">
                <p className="text-brand-charcoal/60 text-sm mb-4" data-id="element-703">
                  Have questions about our cancellation policy?
                </p>
                <button onClick={() => onNavigate('contact')} className="inline-flex items-center justify-center px-8 py-3 bg-brand-red text-white rounded-xl font-bold hover:bg-red-700 transition-colors shadow-md" data-id="element-704">
                  Contact Us
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </main>;
}